import { BasiqJobType } from "../../../../models/types/basiq.types";
import { AccountView } from "../../../../models/view/account/account.view";
import { GlossSyncService } from "../../gloss.sync.service";
import { inject, Injectable } from "@angular/core";
import { BasiqAccountSync } from "./basiq.account.sync";
import { BasiqInstitutionAction } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.action.institution";
import { BasiqAccountAction } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.action.account";
import { BasiqJobAction } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.action.job";
import { BasiqJobValidator } from "@bitwarden/web-vault/app/services/syncing/basiq/validators/basiq.validator.job";
import { SyncStatusEnum } from "@bitwarden/web-vault/app/models/types/general-types";

@Injectable({
  providedIn: "root",
})
export class BasiqJobSync {
  constructor(
    private basiqAccountSync: BasiqAccountSync = inject(BasiqAccountSync),
    private glossSyncService: GlossSyncService = inject(GlossSyncService),
    private basiqInstitutionAction: BasiqInstitutionAction = inject(BasiqInstitutionAction),
    private basiqAccountAction: BasiqAccountAction = inject(BasiqAccountAction),
    private basiqJobAction: BasiqJobAction = inject(BasiqJobAction),
    private basiqJobValidator: BasiqJobValidator = inject(BasiqJobValidator),
  ) {}

  /** Run syncing functionality */
  async run(job: BasiqJobType, accounts: AccountView[]) {
    try {
      if (!(await this.basiqJobValidator.isValid(job))) {
        await this.glossSyncService.terminateSync(accounts, this.basiqJobValidator.syncStatus);
        return;
      }
      await this.runAccountLevelSync(accounts);
    } catch (error) {
      await this.glossSyncService.logError(
        accounts,
        error,
        `Syncing from Basiq on Job level(caught error), job:${job}`,
        SyncStatusEnum.unknownError,
      );
    }
  }

  /** Import data for the first time when user give Basiq permissions and get back to Gloss from Basiq */
  async import(jobIds: string) {
    try {
      const jobs = await this.basiqJobAction.getBasiqJobs(jobIds);
      if (!jobs || !jobs?.length) {
        return;
      }
      await this.basiqJobValidator.resolveJobs(jobs);
      if (!(await this.saveInstitutions())) {
        return;
      }
      const accounts = await this.basiqAccountAction.saveAccounts();
      if (accounts && accounts?.length) {
        this.glossSyncService.runningSyncState.set({
          ...this.glossSyncService.runningSyncState(),
          accountsCount: accounts.length,
          failedAccountsCount: 0,
          successfulAccountsCount: 0,
        });
        await this.runAccountLevelSync(accounts);
      }
    } catch (error) {
      this.glossSyncService.runningSyncState.set({
        ...this.glossSyncService.runningSyncState(),
        isRunning: false,
        isError: true,
        message: "somethingWentWrong",
      });
      await this.glossSyncService.logError(
        [],
        error,
        `importing from basiq for jobs: ${jobIds}`,
        null,
      );
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  private async saveInstitutions() {
    try {
      /** Even if job fails we can still save institution from connection itself */
      const isInstosSaved = await this.basiqInstitutionAction.saveInstitutions();
      if (!isInstosSaved) {
        const error = new Error("Could not save institutions, so terminating import.");
        await this.glossSyncService.logError([], error, `Saving institutions from Basiq`, null);
        this.glossSyncService.confirmationDialog.notify(
          "info",
          "info",
          "canNotContinueImportAsInstitutionsWereNotSaved",
        );
        return false;
      }

      return true;
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        `Saving institutions from Basiq(Error Caught)`,
        null,
      );
      this.glossSyncService.confirmationDialog.notify(
        "info",
        "info",
        "canNotContinueImportAsInstitutionsWereNotSaved",
      );
      return false;
    }
  }

  /** Run syncing fora each account */
  private async runAccountLevelSync(accounts: AccountView[]) {
    for (const account of accounts) {
      await this.basiqAccountSync.syncAccount(account);
    }
  }
}
